// AI Tools Database. Warm Minimalism Design
// Role colors: Atlas = deep navy, Comet = warm amber, Manus = forest green

export type Role = "Atlas" | "Comet" | "Manus";
export type SkillLevel = "Beginner" | "Intermediate" | "Advanced";
/**
 * Pricing is a set of tags, not one label.
 *
 * It used to be a single value out of Free / Freemium / Paid / Subscription,
 * which forced a judgement call on every tool that has both a usable free tier
 * and a paid plan, which is almost all of them. "Freemium" and "Subscription" also made
 * the filter bar answer the wrong question: someone filtering for "Free" wants
 * to see everything they can start using today, and Freemium entries were
 * hidden from them.
 *
 * A tool now carries every tag that applies. ChatGPT is ["Free", "Paid"].
 */
export type PricingType = "Free" | "Paid";

export interface Tool {
  id: string;
  name: string;
  description: string;
  useCases: string[];
  pricing: string;
  /** Every tag that applies. Never empty. */
  pricingTypes: PricingType[];
  website: string;
  roles: Role[];
  skillLevel: SkillLevel;
  bestFor: string;
  emoji: string;
  /**
   * Wordmark shown as the card heading, replacing emoji + name.
   *
   * Convention is /assets/logos/<id>.svg. The files committed today are
   * generated placeholders to be swapped for real brand assets. See
   * components/ToolLogo.tsx. Omit this field to fall back to emoji + name.
   */
  logo?: string;
  routeLink?: string;
  /**
   * Whether this entry is shown anywhere in the UI.
   *
   * Added during the Hostinger port so an entry can be taken off the site
   * without deleting its copy or breaking its /resources/<id> URL. Defaults to
   * true when omitted, so existing entries are unaffected.
   *
   * Note: Manus AI (id "manus") is deliberately left published. Caren's call,
   * 13 Aug 2026, despite the Manus shutdown on the 22nd. Setting isPublished
   * to false here is the one-line change if that decision is revisited.
   */
  isPublished?: boolean;
}

/** An entry counts as published unless it explicitly says otherwise. */
export const isPublished = (entry: { isPublished?: boolean }): boolean =>
  entry.isPublished !== false;

export const ROLES: Record<Role, { label: string; tagline: string; description: string; color: string; bgColor: string; borderColor: string; dotColor: string }> = {
  Atlas: {
    label: "Atlas",
    tagline: "Organize · Think Deeply",
    description: "Research, planning, structuring, long-form thinking. Use when you're designing a system, planning a project, or connecting ideas.",
    color: "text-atlas",
    bgColor: "bg-atlas/10",
    borderColor: "border-atlas/30",
    dotColor: "bg-atlas",
  },
  Comet: {
    label: "Comet",
    tagline: "Clarify · Look Things Up",
    description: "Fast answers, summaries, and comparisons. Use when you need to summarize a page, compare tools, or get quick context.",
    color: "text-comet",
    bgColor: "bg-comet/10",
    borderColor: "border-comet/30",
    dotColor: "bg-comet",
  },
  Manus: {
    label: "Manus",
    tagline: "Create · Produce",
    description: "Turn ideas into usable output. Use when you're writing content, repurposing ideas, drafting emails, or creating images and video.",
    color: "text-manus",
    bgColor: "bg-manus/10",
    borderColor: "border-manus/30",
    dotColor: "bg-manus",
  },
};

export const tools: Tool[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    description: "ChatGPT is OpenAI's AI assistant for writing, research, and getting things done. It holds complex conversations, analyzes your files, browses the web, and through ChatGPT Work and Codex it can now take multi-step actions on your behalf.",
    useCases: ["Content creation", "Answering questions", "Writing assistance", "Deep research", "Agentic tasks"],
    pricing: "Freemium. Free tier, $8/mo Go, $20/mo Plus",
    pricingTypes: ["Free", "Paid"],
    website: "https://chatgpt.com/",
    roles: ["Atlas", "Comet", "Manus"],
    skillLevel: "Beginner",
    bestFor: "Best for: generating text, answering questions, and assisting with creative and technical writing",
    emoji: "💬",
    logo: "/assets/logos/chatgpt.svg",
    routeLink: "/resources/chatgpt",
  },
  {
    id: "claude",
    name: "Claude",
    description: "Claude is Anthropic's AI assistant, built for thoughtful conversation and for real work. It excels at long-form analysis, careful reasoning, and writing in a clear, natural voice, and with Claude Cowork it can manage your files, connect to your other apps, and finish multi-step tasks in the background.",
    useCases: ["Complex analysis", "Long-form writing", "Research", "Problem-solving", "File and task automation"],
    pricing: "Freemium. Free tier, $20/mo Pro, $100/mo Max",
    pricingTypes: ["Free", "Paid"],
    website: "https://claude.ai/",
    roles: ["Atlas", "Comet", "Manus"],
    skillLevel: "Intermediate",
    bestFor: "Best for: deep analysis, nuanced writing, and complex problem-solving",
    emoji: "🧡",
    logo: "/assets/logos/claude.svg",
    routeLink: "/resources/claude",
  },
  {
    id: "perplexity",
    name: "Perplexity",
    description: "Perplexity is an AI research platform that searches the web in real time and gives you cited, accurate answers. Its Comet browser goes further, acting as an agent that can browse, compare, and complete tasks for you.",
    useCases: ["Research", "Fact-checking", "Summarizing articles", "Comparing options", "Agentic browsing"],
    pricing: "Freemium. Free tier, $20/mo Pro, $200/mo Max",
    pricingTypes: ["Free", "Paid"],
    website: "https://pplx.ai/carenglasser",
    roles: ["Atlas", "Comet"],
    skillLevel: "Beginner",
    bestFor: "Best for: cited, real-time answers to research questions",
    emoji: "🔍",
    logo: "/assets/logos/perplexity.svg",
    routeLink: "/resources/perplexity",
  },
  {
    id: "manus",
    name: "Manus",
    description: "Manus is an autonomous AI agent that does not just answer, it acts. It builds websites, analyzes data, runs Wide Research across hundreds of sources, and operates a live browser to finish multi-step tasks from start to finish on your behalf.",
    useCases: ["Workflow automation", "Building websites", "Data analysis", "Market research", "Content production"],
    pricing: "Freemium. Free tier, paid plans from $20/mo",
    pricingTypes: ["Free", "Paid"],
    website: "https://manus.im/invitation/TDNFQJ04TL7FG",
    roles: ["Atlas", "Manus"],
    skillLevel: "Intermediate",
    bestFor: "Best for: automating complex multi-step tasks and producing diverse outputs",
    emoji: "🤖",
    logo: "/assets/logos/manus.svg",
    routeLink: "/resources/manus",
  },
  {
    id: "gemini-notebook",
    name: "Gemini Notebook",
    description: "Gemini Notebook, formerly NotebookLM, is Google's AI research companion for your own documents. Upload PDFs, notes, or articles and it becomes an expert on your material, generating summaries, audio and video overviews, and deep research reports.",
    useCases: ["Analyzing documents", "Research synthesis", "Creating summaries", "Audio and video overviews", "Deep research reports"],
    pricing: "Freemium. Free tier, paid limits via Google AI plans from $4.99/mo",
    pricingTypes: ["Free", "Paid"],
    website: "https://notebooklm.google/",
    roles: ["Atlas", "Comet"],
    skillLevel: "Beginner",
    bestFor: "Best for: researchers and learners who need to analyze and synthesize their own documents",
    emoji: "📓",
    logo: "/assets/logos/gemini-notebook.svg",
    routeLink: "/resources/gemini-notebook",
  },
  {
    id: "gemini",
    name: "Gemini",
    description: "Gemini is Google's multimodal AI assistant, working across text, images, video, and audio. It integrates deeply with Gmail, Docs, and Drive, and adds Deep Research, image and video generation, and Gemini in Chrome.",
    useCases: ["Writing assistance", "Summarizing emails", "Deep research", "Image and video generation", "Google Workspace integration"],
    pricing: "Freemium. Free tier, $4.99/mo AI Plus, $19.99/mo AI Pro",
    pricingTypes: ["Free", "Paid"],
    website: "https://gemini.google.com/",
    roles: ["Atlas", "Comet", "Manus"],
    skillLevel: "Beginner",
    bestFor: "Best for: Google Workspace users wanting AI built into their existing workflow",
    emoji: "✨",
    logo: "/assets/logos/gemini.svg",
    routeLink: "/resources/gemini",
  },
  {
    id: "gamma",
    name: "Gamma",
    description: "Gamma turns a simple prompt into polished presentations, documents, websites, and social content in minutes. Gamma Agent then refines the result through conversation, and Gamma Imagine generates the visuals without leaving the app.",
    useCases: ["Presentations", "Pitch decks", "Documents", "Websites", "Social media content"],
    pricing: "Freemium. Free tier, plus paid Plus, Pro, and Ultra plans",
    pricingTypes: ["Free", "Paid"],
    website: "https://gamma.app/signup?r=dbxr5two6g2pqgi",
    roles: ["Manus"],
    skillLevel: "Beginner",
    bestFor: "Best for: quickly generating professional presentations and documents from a simple prompt",
    emoji: "📊",
    logo: "/assets/logos/gamma.svg",
    routeLink: "/resources/gamma",
  },
  {
    /* Added 3 Sep 2026 with Lovable, the site's first two "describe it and it
       builds it" tools. Both are tagged Manus alone: they produce something,
       they do not help you think or look things up, and a role tag that is
       not true makes the quiz recommend them for the wrong question.

       Skill level is the one judgement here. Base44 is Beginner because its
       whole pitch is that it decides the database, the framework and the
       hosting for you; Lovable is Intermediate because it hands you a GitHub
       repository, a credit budget and a documented point where it stops being
       able to help. */
    id: "base44",
    name: "Base44",
    description: "Base44 turns a description of an app into a working one, with the database, the sign-in and the hosting decided for you rather than by you. Discussion Mode lets you plan the build before it costs a credit, an Idea Library gets you past the blank prompt, and Superagents keep tasks running in the background once the app is live.",
    useCases: ["Internal business tools", "Prototypes and MVPs", "Client-facing web apps", "Booking and CRM tools", "Background automations"],
    pricing: "Freemium. Free tier with monthly message credits, plus paid plans; building the app and running it draw on separate credit types.",
    pricingTypes: ["Free", "Paid"],
    website: "https://base44.com/",
    roles: ["Manus"],
    skillLevel: "Beginner",
    bestFor: "Best for: getting a working app built tonight when the point is proving the idea, not shipping production software",
    emoji: "\u{1F9F1}",
    logo: "/assets/logos/base44.svg",
    routeLink: "/resources/base44",
  },
  {
    id: "lovable",
    name: "Lovable",
    description: "Lovable builds real software from plain language: a front end, a database, sign-in and a live URL, with every project synced to a GitHub repository from day one so nothing you build is trapped inside it. Since March 2026 it also handles general business work \u2014 analysis, decks, marketing workflows \u2014 through the same describe-it-and-get-it approach.",
    useCases: ["Web apps and dashboards", "Booking flows and marketplaces", "Client MVPs", "Brand-consistent internal tools", "Business analysis and decks"],
    pricing: "Freemium. Free tier with daily build credits that reset without rolling over, plus paid plans on one shared credit balance.",
    pricingTypes: ["Free", "Paid"],
    website: "https://lovable.dev/",
    roles: ["Manus"],
    skillLevel: "Intermediate",
    bestFor: "Best for: turning a someday project into something people can click on, without betting it on one platform keeping you",
    emoji: "\u{1F497}",
    logo: "/assets/logos/lovable.svg",
    routeLink: "/resources/lovable",
  },
  {
    id: "castmagic",
    name: "Castmagic",
    description: "Castmagic is an AI content engine that turns podcasts, videos, meetings, and webinars into a full library of ready-to-use content: transcripts, show notes, social posts, newsletters, and images. A Magic Chat assistant and a Claude connector let you query your whole back catalogue.",
    useCases: ["Podcast repurposing", "Meeting and webinar recordings", "Transcription", "Show notes", "Social media posts"],
    pricing: "Paid. 7-day free trial, then from $19/mo billed annually",
    pricingTypes: ["Paid"],
    website: "https://get.castmagic.io/carenglasser",
    roles: ["Manus"],
    skillLevel: "Beginner",
    bestFor: "Best for: podcasters, coaches, and marketers who want to repurpose long-form recordings automatically",
    emoji: "🎙️",
    logo: "/assets/logos/castmagic.svg",
    routeLink: "/resources/castmagic",
  },
  {
    id: "opus-clip",
    name: "Opus Clip",
    description: "Opus Clip uses AI to find the most engaging moments in your long videos and turns them into short-form clips ready for TikTok, Instagram Reels, and YouTube Shorts, complete with captions, reframing, and virality scoring. Paid plans can auto-post them for you.",
    useCases: ["Short-form video clips", "Social media content", "AI captions", "Video repurposing", "Auto-posting and scheduling"],
    pricing: "Freemium. Free tier, paid plans from $15/mo",
    pricingTypes: ["Free", "Paid"],
    website: "https://www.opus.pro/?via=carenglasser",
    roles: ["Manus"],
    skillLevel: "Beginner",
    bestFor: "Best for: content creators who want to repurpose long videos into viral short clips",
    emoji: "✂️",
    logo: "/assets/logos/opus-clip.svg",
    routeLink: "/resources/opus-clip",
  },
  {
    id: "agent-opus",
    name: "Agent Opus",
    description: "Agent Opus is an AI video agent that handles the entire video creation process from a single idea, covering research, scripting, motion graphics, avatars, voice cloning, and editing. Style Maker builds a custom look from your own reference images so every video stays on brand.",
    useCases: ["End-to-end video creation", "AI avatars", "Voice cloning", "Motion graphics", "Social media videos"],
    pricing: "Freemium. Free tier, $29/mo Pro, $129/mo Max",
    pricingTypes: ["Free", "Paid"],
    website: "https://www.opus.pro/agent?ref_id=9J4U2I5N5",
    roles: ["Manus"],
    skillLevel: "Intermediate",
    bestFor: "Best for: creators who want to produce polished videos from a single idea without a production team",
    emoji: "🎬",
    logo: "/assets/logos/agent-opus.svg",
    routeLink: "/resources/agent-opus",
  },
  {
    id: "littlebird",
    name: "Littlebird",
    description: "Littlebird is an always-on, context-aware AI assistant that reads your screen and transcribes your meetings. It builds a private memory of your work, so it already knows what you are doing and can answer questions, recall information, and automate tasks without you having to explain context. A floating Hummingbird overlay puts it on top of any app, and Routines deliver briefings on your schedule.",
    useCases: ["Context recall", "Meeting transcription", "Task automation", "Scheduled briefings", "Information retrieval"],
    pricing: "Freemium. Free tier, Plus from $17/mo billed annually",
    pricingTypes: ["Free", "Paid"],
    website: "https://try.littlebird.ai/caren-glasser",
    roles: ["Atlas", "Comet"],
    skillLevel: "Beginner",
    bestFor: "Best for: professionals who want an AI that understands their full work context and can recall anything from their screen",
    emoji: "🐦",
    logo: "/assets/logos/littlebird.svg",
    routeLink: "/resources/littlebird",
  },
];

/**
 * How many tools the directory actually shows.
 *
 * Derived, because it was typed out. A fixed count was written into the footer,
 * the auth screens and My Stack, and unpublishing Grok made all three wrong at
 * once — copy promising a database one item larger than the grid it links to.
 * Nothing would have failed; the number would simply have been a lie in three
 * places until somebody counted the cards.
 *
 * Anything user-facing that states the size of the directory reads this.
 */
export const PUBLISHED_TOOL_COUNT: number = tools.filter(isPublished).length;

export const PRICING_TYPES: PricingType[] = ["Free", "Paid"];
export const SKILL_LEVELS: SkillLevel[] = ["Beginner", "Intermediate", "Advanced"];
export const ALL_ROLES: Role[] = ["Atlas", "Comet", "Manus"];
